import type { CitationContext, SelectedTextData } from '~/utils/copy-with-source'
import { consola } from 'consola'
import {
  buildCopyPayload,
  clearBrowserTextSelection,
  detectCitationFromSelection,
  isValidTextSelection,
} from '~/utils/copy-with-source'
import { SELECTION_SETTLE_DELAY_MS, SELECTION_TOOLTIP_OFFSET, TOOLTIP_VIEWPORT_MARGIN } from '~/constants/markings'
import { clampTooltipToViewport, closestMarkElement, expandRangeToWordBoundaries } from '~/utils/markings'

export function useSelectionTooltip(containerKeys: MaybeRefOrGetter<readonly ContainerKey[]>) {
  const showTooltip = ref(false)
  const tooltipCoords = ref({ x: 0, y: 0 })
  const selectedTextData = ref<SelectedTextData | null>(null)
  // The live selection clears on mousedown on a tooltip button — capture it.
  const capturedRange = ref<Range | null>(null)
  const tooltipRef = ref<HTMLElement>()

  const { copy, isSupported: clipboardSupported } = useClipboard()
  const isTouchPointer = useMediaQuery('(pointer: coarse)')
  const { text: selectionText, ranges: selectionRanges } = useTextSelection()
  const containers = inject(diKeys.containers)

  function hideTooltip() {
    showTooltip.value = false
    selectedTextData.value = null
    capturedRange.value = null
  }

  function clearSelection() {
    clearBrowserTextSelection()
    hideTooltip()
  }

  function handleTextSelection(mouseX: number, mouseY: number) {
    // Touch devices keep their own selection handles over ours, so a fresh
    // selection cannot reliably become a marking there. Opening the tooltip
    // for existing markings is unaffected — see showTooltipForRange — so they
    // can still be read, recoloured and annotated.
    if (isTouchPointer.value) {
      hideTooltip()
      return
    }

    if (!isValidTextSelection(selectionText.value, selectionRanges.value)) {
      hideTooltip()
      return
    }

    const text = selectionText.value?.trim()
    const range = selectionRanges.value?.[0]

    if (!text || !range) {
      hideTooltip()
      return
    }

    const citationContext = detectCitationFromSelection(range)

    if (!citationContext) {
      hideTooltip()
      return
    }

    const expandedRange = expandRangeToWordBoundaries(range)
    const sel = window.getSelection()

    if (sel) {
      sel.removeAllRanges()
      sel.addRange(expandedRange)
    }

    presentTooltip(expandedRange, citationContext, text, { x: mouseX, y: mouseY })
  }

  function presentTooltip(range: Range, citationContext: CitationContext, text: string, mouse: { x: number, y: number }) {
    tooltipCoords.value = {
      x: Math.max(TOOLTIP_VIEWPORT_MARGIN, mouse.x + SELECTION_TOOLTIP_OFFSET.x),
      y: mouse.y + SELECTION_TOOLTIP_OFFSET.y,
    }

    selectedTextData.value = buildCopyPayload(range, citationContext, text)
    capturedRange.value = range.cloneRange()
    showTooltip.value = true

    nextTick(() => {
      if (tooltipRef.value) {
        tooltipCoords.value = clampTooltipToViewport(tooltipRef.value, tooltipCoords.value)
      }
    })
  }

  /**
   * Opens the tooltip for an existing DOM range (a clicked marking) instead
   * of a live text selection. Returns false when the range has no citation
   * context — the tooltip's actions wouldn't work without one.
   */
  function showTooltipForRange(range: Range, mouseX: number, mouseY: number): boolean {
    const text = range.toString().trim()

    if (!text) {
      return false
    }

    const citationContext = detectCitationFromSelection(range)

    if (!citationContext) {
      return false
    }

    presentTooltip(range, citationContext, text, { x: mouseX, y: mouseY })
    return true
  }

  async function copySelection() {
    if (!selectedTextData.value) {
      return
    }

    const { plainText, htmlPayload } = selectedTextData.value

    try {
      const clipboardItem = new ClipboardItem({
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
        'text/html': new Blob([htmlPayload], { type: 'text/html' }),
      })
      await navigator.clipboard.write([clipboardItem])
      clearSelection()
    }
    catch (error) {
      consola.error('Failed to copy to clipboard:', error)

      if (clipboardSupported.value) {
        await copy(plainText)
        clearSelection()
      }
    }
  }

  useEventListener(document, 'mouseup', (event: MouseEvent) => {
    const target = event.target

    if (!(target instanceof Node) || tooltipRef.value?.contains(target)) {
      return
    }

    for (const containerKey of toValue(containerKeys)) {
      const container = toValue(containers?.[containerKey])

      if (container?.contains(target)) {
        setTimeout(() => {
          // A plain click on marked text is handled by the marking click
          // listener (which reopens the tooltip for that marking) — hiding
          // here would race it.
          const isMarkClick = target instanceof Element
            && closestMarkElement(target)
            && window.getSelection()?.isCollapsed

          if (isMarkClick) {
            return
          }

          handleTextSelection(event.clientX, event.clientY)
        }, SELECTION_SETTLE_DELAY_MS)
        break
      }
    }
  }, { capture: true })

  onClickOutside(tooltipRef, hideTooltip)

  useEventListener(document, 'scroll', () => {
    if (showTooltip.value) {
      hideTooltip()
    }
  }, { passive: true, capture: true })

  return {
    capturedRange,
    clearSelection,
    copySelection,
    hideTooltip,
    selectedTextData,
    selectionRanges,
    showTooltip,
    showTooltipForRange,
    tooltipCoords,
    tooltipRef,
  }
}
